import React from "react"
import moment from "moment"
import { Button, Card, Col, DatePicker, Form, Input, Row, Segmented, Space, Statistic, Table, Tag, Typography } from "antd"
import { ReloadOutlined } from "@ant-design/icons"
import { useDebts } from "./model"
import { DebtChart } from "./debt-chart"
import { DebtUserDrawer } from "./debt-user-drawer"
import { COLORS, formatDate, formatPoints } from "./format"

const STATUS_OPTIONS = [
  { value: "in_debt", label: "В минусе" },
  { value: "recovered", label: "Погасили" },
  { value: "", label: "Все" },
]

export function DebtsTab() {
  const [userId, setUserId] = React.useState(null)
  const {
    summary,
    daily,
    period,
    users,
    pagination,
    status,
    sort,
    isLoading,
    isUsersLoading,
    handlePeriod,
    handleStatus,
    handleFilters,
    handleTableChange,
    loadUser,
  } = useDebts()

  // Сортировка на сервере: без id, телефона, почты и статуса.
  const sortable = (dataIndex) => ({
    sorter: true,
    sortOrder: sort?.field === dataIndex ? sort.order : null,
  })

  const columns = [
    { title: "Пользователь", dataIndex: "user_id" },
    { title: "Телефон", dataIndex: "phone", render: (value) => value || "—" },
    { title: "Почта", dataIndex: "email", render: (value) => value || "—" },
    {
      title: "Списано за сбой",
      dataIndex: "written_off_points",
      align: "right",
      render: formatPoints,
      ...sortable("written_off_points"),
    },
    {
      title: "Долг после списания",
      dataIndex: "initial_debt",
      align: "right",
      render: formatPoints,
      ...sortable("initial_debt"),
    },
    {
      title: "Долг сейчас",
      dataIndex: "current_debt",
      align: "right",
      render: (value) => <span style={{ color: value > 0 ? COLORS.debt : undefined }}>{formatPoints(value)}</span>,
      ...sortable("current_debt"),
    },
    {
      title: "Погашено",
      dataIndex: "recovered_percent",
      align: "right",
      render: (value) => `${value}%`,
      ...sortable("recovered_percent"),
    },
    { title: "Проверен в CRM", dataIndex: "last_checked_at", render: formatDate, ...sortable("last_checked_at") },
    {
      title: "Статус",
      dataIndex: "status",
      render: (value, row) =>
        value === "recovered" ? (
          <Tag color="success">Погашен {formatDate(row.recovered_at).slice(0, 10)}</Tag>
        ) : (
          <Tag color="error">В минусе</Tag>
        ),
    },
  ]

  return (
    <>
      <Card size="small" style={{ marginBottom: 16 }} loading={isLoading && !summary}>
        {summary && (
          <Row gutter={[24, 16]}>
            <Col xs={12} md={4}>
              <Statistic title="Должников сейчас" value={summary.in_debt} />
            </Col>
            <Col xs={12} md={4}>
              <Statistic title="Долг сейчас" value={summary.current_debt} precision={2} suffix="₽" valueStyle={{ color: COLORS.debt }} />
            </Col>
            <Col xs={12} md={4}>
              <Statistic title="Погашено" value={summary.recovered_points} precision={2} suffix="₽" valueStyle={{ color: COLORS.accrued }} />
            </Col>
            <Col xs={12} md={4}>
              <Statistic title="Погасили полностью" value={summary.recovered_users} suffix={`из ${summary.debtors_total}`} />
            </Col>
            <Col xs={12} md={4}>
              <Statistic
                title={`Гасится в день (за ${summary.forecast_window_days} дн.)`}
                value={summary.recovery_per_day}
                precision={2}
                suffix="₽"
              />
            </Col>
            <Col xs={12} md={4}>
              <Statistic
                title="Прогноз полного погашения"
                value={summary.forecast_recovered_at ? moment(summary.forecast_recovered_at).format("DD.MM.YYYY") : "нет прогноза"}
                valueStyle={{ fontSize: 18 }}
              />
            </Col>
          </Row>
        )}
      </Card>

      <Form
        layout="inline"
        style={{ marginBottom: 16 }}
        initialValues={{ range: [moment(period.dateFrom), moment(period.dateTo)] }}
        onFinish={(values) =>
          handlePeriod({
            dateFrom: values.range[0].format("YYYY-MM-DD"),
            dateTo: values.range[1].format("YYYY-MM-DD"),
          })
        }
      >
        <Form.Item name="range" label="Период">
          <DatePicker.RangePicker format="DD.MM.YYYY" allowClear={false} />
        </Form.Item>
        <Form.Item>
          <Button type="primary" htmlType="submit" icon={<ReloadOutlined />} loading={isLoading}>
            Показать
          </Button>
        </Form.Item>
      </Form>

      <DebtChart rows={daily} forecastDate={summary?.forecast_recovered_at} />

      <Space wrap style={{ marginBottom: 12 }}>
        <Segmented options={STATUS_OPTIONS} value={status} onChange={handleStatus} />
        <Input.Search
          placeholder="Телефон"
          allowClear
          maxLength={32}
          style={{ width: 200 }}
          onSearch={(value) => handleFilters({ phone: value.trim() })}
        />
        <Input.Search
          placeholder="Почта"
          allowClear
          maxLength={255}
          style={{ width: 240 }}
          onSearch={(value) => handleFilters({ email: value.trim() })}
        />
      </Space>
      <Table
        size="small"
        rowKey="user_id"
        loading={isUsersLoading}
        dataSource={users}
        columns={columns}
        pagination={pagination}
        onChange={handleTableChange}
        onRow={(row) => ({ onClick: () => setUserId(row.user_id), style: { cursor: "pointer" } })}
      />

      <Typography.Text type="secondary" style={{ display: "block", marginTop: 8 }}>
        Долг — баллы, списанные за технический сбой сверх баланса. Баланс должников проверяется в CRM раз в день
        {summary?.last_checked_at ? ` (последняя проверка ${formatDate(summary.last_checked_at)} UTC)` : ""}. Прогноз — по
        средней скорости погашения. 1 балл = 1 ₽.
      </Typography.Text>

      <DebtUserDrawer userId={userId} loadUser={loadUser} onClose={() => setUserId(null)} />
    </>
  )
}
